import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { DEVI_FORMS, deviImageFor } from "@/lib/deviForms";
import { Sparkles, ArrowRight } from "lucide-react";

// Rotating "Devi Form of the Day" — picks one of the nine Navadurga forms by the
// day of the year, so the homepage always features a different devi with her art.
// During Navratri itself, TodaysDeviForm shows the exact night's form, so this
// hides to avoid a duplicate card.
const NAV_START = new Date("2026-10-11T00:00:00+05:30");
function inNavratri() {
  const diff = Math.floor((new Date() - NAV_START) / 86400000);
  return diff >= 0 && diff <= 8;
}
function formOfTheDay() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return DEVI_FORMS[dayOfYear % DEVI_FORMS.length];
}

export default function DeviFormOfDay() {
  const { pick, isDeva } = useLang();
  const settings = useSettings();
  if (inNavratri()) return null;
  const f = formOfTheDay();
  const img = deviImageFor(f, settings);

  return (
    <section className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16" data-testid="devi-form-of-day">
      <Link to={`/navratri/${f.slug}`}
        className="group grid overflow-hidden rounded-2xl border border-line bg-[var(--surface)] shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-xl md:grid-cols-[0.9fr_1.1fr]">
        <div className="relative aspect-[3/4] overflow-hidden md:aspect-auto">
          <img src={img} alt={f.en} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent md:bg-gradient-to-r" />
          <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-1 text-xs font-medium text-white backdrop-blur"><Sparkles className="h-3.5 w-3.5 text-[var(--gold)]" /> {pick("आज की देवी", "Devi of the Day")}</span>
        </div>
        <div className="flex flex-col justify-center p-6 md:p-10" style={{ borderTop: `3px solid ${f.accent}` }}>
          <p className="overline-label" style={{ color: f.accent }}>{pick("नवदुर्गा", "Navadurga")} · {pick("स्वरूप", "Form")} {f.day}</p>
          <h2 className={`mt-2 text-4xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick(f.deva, f.en)}</h2>
          <p className={`text-xl text-ink-2 ${isDeva ? "font-serif-latin" : "font-deva deva-lh"}`}>{pick(f.en, f.deva)}</p>
          <p className="mt-4 max-w-[48ch] text-[15px] leading-relaxed text-ink-2">{pick(f.story_hi, f.story_en)}</p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-[var(--parchment)] px-3 py-1 text-ink-2">{pick("रंग", "Colour")}: {pick(f.color_hi, f.color_en)}</span>
            <span className="rounded-full bg-[var(--parchment)] px-3 py-1 text-ink-2">{pick("भोग", "Bhog")}: {pick(f.bhog_hi, f.bhog_en)}</span>
            <span className="rounded-full bg-[var(--parchment)] px-3 py-1 font-deva text-ink-2">{f.beej}</span>
          </div>
          <span className="mt-5 inline-flex items-center gap-1.5 self-start border border-saffron px-5 py-2.5 text-sm font-medium text-saffron transition-colors group-hover:bg-[var(--saffron)] group-hover:text-white">
            {pick("दर्शन एवं पूजा विधि", "Darshan & worship guide")} <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </Link>
    </section>
  );
}
